import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import TemplateForm from '../../../components/WhatsApp/TemplateForm';
import WhatsAppTemplatePreview from '../../../components/WhatsApp/TemplatePreview';
import { getRequest, postRequest, postRequestAsync } from '../../../helpers/apiHandler';
import useFBAuthenticated from '../../../hooks/useFBAuthenticated';
import useWhatsAppTemplate from '../../../hooks/useWhatsAppTemplate';
import { showAlert } from '../../../store/actions/alertAction';

const WhatsAppTemplateEditor = () => {
  const { waba_id, phone_number_id, template_id } = useParams();
  const { access_token } = useSelector((state) => state.auth.user);
  const { isAuthenticated: isFBAuthenticated } = useFBAuthenticated(access_token);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoaded, setIsLoaded] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const {
    template,
    templateHeader,
    templateBody,
    templateFooter,
    templateButtons,
    bodyVariables,
    bodyVariableEntries,
    codeExp,
    bodyTextareaRef,
    isUploading,
    uploadError,
    securityRecommendation,
    showExpiryTime,
    handleTemplateChange,
    handleBodyChange,
    handleMediaUpload,
    setTemplateHeader,
    setTemplateFooter,
    setCodeExp,
    handleAddVariable,
    handleRemoveVariable,
    handleVariableEntryChange,
    handleButtonChange,
    handleAddButton,
    handleRemoveButton,
    handleTextFormat,
    validateTemplate,
    getTemplatePayload,
  } = useWhatsAppTemplate();

  const fetchTemplateDetails = () => {
    setIsLoading(true);

    postRequest('/v2/whatsapp/wa-template-details', { template_id }, access_token.token, (res) => {
      if (res.success) {
        const { template } = res.data;
        const components = template.components || [];

        const componentData = components.reduce(
          (extractedData, component) => {
            switch (component.type) {
              case 'HEADER':
                if (component.format === 'TEXT') {
                  extractedData.header = component.text || '';
                } else if (
                  component.format === 'IMAGE' &&
                  component.example &&
                  component.example.header_handle
                ) {
                  extractedData.headerType = 'IMAGE';

                  if (
                    Array.isArray(component.example.header_handle) &&
                    component.example.header_handle.length > 0
                  ) {
                    extractedData.headerMediaPreviewUrl = component.example.header_handle[0];
                    extractedData.headerMediaHandle = component.example.header_handle;
                  } else if (typeof component.example.header_handle === 'string') {
                    extractedData.headerMediaPreviewUrl = component.example.header_handle;
                    extractedData.headerMediaHandle = component.example.header_handle;
                  }
                }
                break;
              case 'BODY':
                extractedData.body = component.text || '';
                if (
                  component.example &&
                  component.example.body_text &&
                  Array.isArray(component.example.body_text) &&
                  component.example.body_text.length > 0 &&
                  Array.isArray(component.example.body_text[0])
                ) {
                  extractedData.bodyExamples = component.example.body_text[0];
                }
                break;
              case 'FOOTER':
                extractedData.footer = component.text || '';
                break;
              case 'BUTTONS':
                extractedData.buttons = component.buttons || [];
                break;
              default:
                break;
            }
            return extractedData;
          },
          { header: '', body: '', footer: '', buttons: [], bodyExamples: [] }
        );

        const templateData = {
          name: template.name,
          language: template.language,
          category: template.category,
          header: componentData.header,
          body: componentData.body,
          footer: componentData.footer,
          bodyExamples: componentData.bodyExamples,
          buttons: componentData.buttons,
          headerType: componentData.headerType,
          headerMediaPreviewUrl: componentData.headerMediaPreviewUrl,
          headerMediaHandle: componentData.headerMediaHandle,
        };

        const processTemplateData = async () => {
          handleTemplateChange('name', templateData.name);
          handleTemplateChange('language', templateData.language);
          handleTemplateChange('category', templateData.category);

          if (templateData.headerType === 'IMAGE') {
            handleTemplateChange('headerType', 'IMAGE');
            handleTemplateChange('headerMediaHandle', templateData.headerMediaHandle);
            handleTemplateChange('headerMediaPreviewUrl', templateData.headerMediaPreviewUrl);
          } else {
            setTemplateHeader(templateData.header);
          }

          const bodyText = templateData.body;
          const variableMatches = bodyText.match(/{{[0-9]+}}/g) || [];
          const expectedVariableCount = variableMatches.length;

          handleBodyChange(templateData.body);

          setTemplateFooter(templateData.footer);

          if (templateData.bodyExamples && templateData.bodyExamples.length > 0) {
            await new Promise((resolve) => setTimeout(resolve, 300));

            if (variableMatches.length > 0) {
              variableMatches.forEach((_variable, index) => {
                if (index < templateData.bodyExamples.length) {
                  const exampleValue = templateData.bodyExamples[index];
                  handleVariableEntryChange(index, exampleValue);
                }
              });
            }
          }

          const currentButtonCount = templateButtons.length;
          for (let i = currentButtonCount - 1; i >= 0; i--) {
            handleRemoveButton(i);
          }

          if (
            templateData.category !== 'AUTHENTICATION' &&
            templateData.buttons &&
            templateData.buttons.length > 0
          ) {
            for (let i = 0; i < templateData.buttons.length; i++) {
              const button = templateData.buttons[i];

              handleAddButton();

              await new Promise((resolve) => setTimeout(resolve, 100));

              handleButtonChange(i, 'type', button.type);
              handleButtonChange(i, 'text', button.text || '');

              if (button.type === 'URL' && button.url) {
                handleButtonChange(i, 'url', button.url);
              }

              if (button.type === 'PHONE_NUMBER' && button.phone_number) {
                handleButtonChange(i, 'phoneNumber', button.phone_number);
              }

              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          }

          if (templateData.category === 'AUTHENTICATION' && templateData.footer) {
            const match = templateData.footer.match(/\d+/);
            if (match) {
              setCodeExp(parseInt(match[0]));
            }
          }

          setIsLoading(false);
        };

        processTemplateData();
      } else {
        console.error('[TemplateEditor] Failed to fetch template details:', res.message);
        dispatch(showAlert('Failed to load template details', 'danger'));
        setIsLoading(false);
      }
    });
  };

  const fetchTemplateLanguages = () => {
    const requestData = { headers: { Authorization: access_token.token } };

    getRequest('/v2/whatsapp/wa-template-languages', requestData, (res) => {
      if (res.success) {
        const { languages } = res.data;
        setLanguages(languages);
      } else {
        console.error('[TemplateEditor] Failed to fetch template languages:', res.message);
      }
    });
  };

  useEffect(() => {
    if (isFBAuthenticated && !isLoaded) {
      setIsLoading(true);

      fetchTemplateLanguages();

      fetchTemplateDetails();

      setIsLoaded(true);
    }
  }, [isFBAuthenticated, isLoaded]);

  useEffect(() => {
    if (template.language !== '' && languages.length > 0) {
      const foundLanguage = languages.find((lang) => lang.value === template.language);
      if (foundLanguage) {
        setSelectedLanguage([foundLanguage]);
      }
    }
  }, [template.language, languages]);

  const handleLanguageChange = (opt) => {
    handleTemplateChange('language', opt[0].value);
    setSelectedLanguage(opt);
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    if (!validateTemplate()) {
      dispatch(showAlert('Please fill in all required fields', 'danger'));
      return;
    }

    setIsSubmitting(true);

    const requestData = {
      template_id,
      ...getTemplatePayload(),
    };

    try {
      postRequest('/v2/whatsapp/wa-template-update', requestData, access_token.token, (res) => {
        if (res.success) {
          dispatch(showAlert(res.message, 'success'));
          navigate(`/dashboard/whatsapp/phone-number-details/${waba_id}/${phone_number_id}`);
        } else {
          dispatch(showAlert(res.message, 'danger'));
          setIsSubmitting(false);
        }
      });
    } catch (error) {
      console.error('[TemplateEditor] Exception during template update:', error);
      dispatch(showAlert('An error occurred while updating the template', 'danger'));
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) {
      return;
    }

    if (window.confirm('Are you sure you want to delete this template?')) {
      setIsDeleting(true);

      try {
        const res = await postRequestAsync(
          '/v2/whatsapp/wa-template-delete',
          { template_id, name: template.name, waba_id },
          access_token.token
        );
        if (res.success) {
          dispatch(showAlert('Template deleted successfully', 'success'));
          navigate(`/dashboard/whatsapp/phone-number-details/${waba_id}/${phone_number_id}`);
        } else {
          dispatch(showAlert(res.message, 'danger'));
          setIsDeleting(false);
        }
      } catch (error) {
        dispatch(showAlert('An error occurred while deleting the template', 'danger'));
        setIsDeleting(false);
      }
    }
  };

  return (
    <Container fluid className="wa-template-editor p-4">
      <Row>
        <Col md={8}>
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status"></Spinner>
            </div>
          ) : (
            <TemplateForm
              isEditing={true}
              template={template}
              templateHeader={templateHeader}
              templateBody={templateBody}
              templateFooter={templateFooter}
              templateButtons={templateButtons}
              bodyVariables={bodyVariables}
              bodyVariableEntries={bodyVariableEntries}
              codeExp={codeExp}
              bodyTextareaRef={bodyTextareaRef}
              languages={languages}
              selectedLanguage={selectedLanguage}
              onLanguageChange={handleLanguageChange}
              onTemplateChange={handleTemplateChange}
              onBodyChange={handleBodyChange}
              setTemplateHeader={setTemplateHeader}
              setTemplateFooter={setTemplateFooter}
              setCodeExp={setCodeExp}
              onAddVariable={handleAddVariable}
              onRemoveVariable={handleRemoveVariable}
              onVariableEntryChange={handleVariableEntryChange}
              onButtonChange={handleButtonChange}
              onAddButton={handleAddButton}
              onRemoveButton={handleRemoveButton}
              onTextFormat={handleTextFormat}
              handleMediaUpload={handleMediaUpload}
              isUploading={isUploading}
              uploadError={uploadError}
            />
          )}

          {/* Action Buttons */}
          <div className="action-buttons mt-4">
            <Button
              variant="danger"
              onClick={handleDelete}
              className="delete-btn"
              disabled={isSubmitting || isDeleting || isLoading}
            >
              {isDeleting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Deleting...
                </>
              ) : (
                'Delete Template'
              )}
            </Button>
            <div className="right-buttons">
              <Button
                variant="outline-primary"
                as={Link}
                to={`/dashboard/whatsapp/phone-number-details/${waba_id}/${phone_number_id}`}
                className="discard-btn"
                disabled={isSubmitting || isLoading}
              >
                Discard
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                className="submit-btn"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  'Submit for Review'
                )}
              </Button>
            </div>
          </div>
        </Col>

        {/* Right Preview Panel */}
        <Col md={4}>
          <div className="preview-container pb-4">
            <WhatsAppTemplatePreview
              header={templateHeader}
              headerType={template.headerType || 'TEXT'}
              headerMediaUrl={template.headerMediaPreviewUrl}
              body={templateBody}
              footer={templateFooter}
              buttons={templateButtons}
              variableEntries={bodyVariableEntries}
              isAuthentication={template.category === 'AUTHENTICATION'}
              securityRecommendation={securityRecommendation}
              codeExp={codeExp}
              showExpiryTime={showExpiryTime}
            />
            <h4 className="text-center">Template Preview</h4>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default WhatsAppTemplateEditor;
